import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

// ClaimUsernamePage — one-step "claim your handle" screen shown right
// after a brand-new X sign-up. The username is pre-filled with the
// user's X handle (auto-deduped server-side, but the user can edit
// before committing).
//
// We don't render this for returning sign-ins — the X callback only
// navigates here when `pending_username=true` in the backend response.
//
// UX choices:
//   - Single input, single button, no clutter. The whole point of
//     the X funnel is to remove friction; making this a multi-step
//     onboarding would undo the win.
//   - Skip option: clicking the X-handle-as-username is the default,
//     pressing "Use this username" or just Enter commits. Users in
//     a rush can hit "Skip → use auto-generated" and edit later from
//     Profile settings.
//
// Backend wiring: PATCH /api/users/me/username (existing endpoint)
// validates uniqueness + length; we surface its 4xx message.
const ClaimUsernamePage = () => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { user, loading, refreshUser } = useAuth();
  const initialHandle = (search.get("x_handle") || user?.username || "").replace(/^@/, "");
  const [handle, setHandle] = useState(initialHandle);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Only redirect after AuthContext has finished its on-mount
    // /auth/me hydration. Otherwise a fast-mount test (or a real user
    // hitting refresh on this page) would briefly observe user=null
    // and bounce them back to landing before the JWT-backed session
    // had a chance to rehydrate.
    if (loading) return undefined;
    if (!user) {
      const t = setTimeout(() => navigate("/", { replace: true }), 50);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const commit = async (e) => {
    e?.preventDefault?.();
    setError("");
    const clean = handle.replace(/^@/, "").trim();
    if (!clean) { setError("Pick any username — your X handle was a suggestion."); return; }
    if (clean === user?.username) {
      // Auto-generated value is fine — just skip to feed.
      navigate("/", { replace: true });
      return;
    }
    setSaving(true);
    try {
      await api.patch("/users/me/username", { username: clean });
      if (typeof refreshUser === "function") await refreshUser();
      toast.success(`Welcome to GlobalEyes, @${clean}!`);
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "That username is taken — try another.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-gray-950 to-black flex items-center justify-center px-4" data-testid="claim-username-page">
      <div className="w-full max-w-md bg-gray-900/80 backdrop-blur border border-gray-800 rounded-xl p-6 shadow-2xl">
        <h1 className="text-xl font-semibold text-white mb-1">One last thing —</h1>
        <p className="text-sm text-gray-400 mb-5">
          Claim your handle on GlobalEyes. We've pre-filled the one you use on X. You can keep it or change it now (you can edit later, too).
        </p>
        <form onSubmit={commit} className="space-y-4">
          <div className="flex items-center gap-1 bg-black/40 border border-gray-700 rounded-md px-3 py-2">
            <span className="text-gray-500 text-sm">@</span>
            <Input
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              autoFocus
              placeholder="your-handle"
              className="bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-white text-base px-1"
              data-testid="claim-username-input"
              maxLength={32}
            />
          </div>
          {error && <p className="text-sm text-rose-400" data-testid="claim-username-error">{error}</p>}
          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="claim-username-submit"
          >
            {saving ? "Saving…" : "Use this username →"}
          </Button>
          <button
            type="button"
            onClick={() => navigate("/", { replace: true })}
            className="block w-full text-center text-xs text-gray-500 hover:text-gray-300 transition-colors"
            data-testid="claim-username-skip"
          >
            Skip for now — I'll set it later
          </button>
        </form>
      </div>
    </div>
  );
};

export default ClaimUsernamePage;
